import noop from 'lodash/noop';
import { createContext } from 'react';

import { FeatureFlagResult } from 'utils/featureFlagging/featureFlags';

import {
  CopilotActionComputed,
  CopilotActions,
  CopilotDropdownFeatureFlag,
} from './copilotDropdown.types';

type UndoStackItem = {
  readonly content: string;
  readonly feedbackId: string | null;
  readonly additionalFeedback: Record<string, string | null>;
};

/** Represents the type of the context provided to copilot actions. */
export type CopilotDropdownActionContextValue = {
  readonly actions: CopilotActions;
  readonly actionsComputed: Record<keyof CopilotActions, CopilotActionComputed>;
  readonly onBack: () => void;
  readonly goToAction: (
    action: string | undefined,
    draftContent: string,
    undoStackAction?: 'push' | 'replace',
  ) => void;
  readonly dropdownUndoStack: UndoStackItem[];
  readonly latestUndoStackItem: undefined | UndoStackItem;
  readonly resetDropdownUndoStack: () => void;
  readonly pushDropdownUndoStack: (
    content: string,
    feedbackId?: string,
    additionalFeedback?: Record<string, string | null>,
  ) => void;
  readonly popDropdownUndoStack: () => undefined | UndoStackItem;
  readonly featureFlags?: FeatureFlagResult<CopilotDropdownFeatureFlag>;
};

/**
 * Provides values and methods to be used in each copilot action or to interact
 * with the root component.
 */
export const CopilotDropdownActionContext =
  createContext<CopilotDropdownActionContextValue>({
    actions: {} as CopilotActions,
    actionsComputed: {} as Record<keyof CopilotActions, CopilotActionComputed>,
    onBack: noop,
    goToAction: noop,
    dropdownUndoStack: [],
    latestUndoStackItem: undefined,
    resetDropdownUndoStack: noop,
    pushDropdownUndoStack: noop,
    popDropdownUndoStack: () => undefined,
    featureFlags: undefined,
  });
